import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';

import { Subscription } from 'rxjs';

import { IconComponent } from '../../components/icon/icon.component';
import { ButtonComponent } from '../../components/button/button.component';
import { Menu, MenuPanelItem, MenuItem, MenuItemType } from '../../domain/menu.domain';
import { SecurityService } from '../../services/security.service';
import { MenuService } from '../../services/menu/menu.service';
import { IfFeatureService } from '../../services/if-feature/if-feature.service';
import { DataAnalyticsTaggingService } from '../data-analytics-tagging/data-analytics-tagging.service';
import { DataAnalyticsTaggingConstants } from '../data-analytics-tagging/data-analytics-tagging.constants';
import { DividerComponent } from '../../components/panel/divider.component';
import { HrefDirective } from '../../directives/href/href.directive';
import { BadgeComponent } from '../../components/badge/badge.component';
import { DataAnalyticsService } from '../../services/data-analytics/data-analytics.service';

import { ClientProfileService } from '../client-profile/client-profile.service';
import { IfFeatureDirective } from '../if-feature/if-feature.directive';
import { IfNoFeatureDirective } from '../if-feature/if-no-feature.directive';
import { MessagesCounterComponent } from '../messages/messages-counter/messages-counter.component';
import { ChatService } from '../chat/chat.service';
import { MobileFlyoutMenuNavPanDirective } from './mobile-menu/mobile-flyout-menu-nav-pan.directive';
import { MobileFlyoutMenuComponent } from './mobile-menu/mobile-flyout-menu.component';
import { DocumentNoScrollDirective } from './mobile-menu/document-no-scroll.directive';
import { MobileFlyoutMenuNavComponent } from './mobile-menu/mobile-flyout-menu-nav.component';
import { MenuSearchNavigationComponent } from './search-navigation/search-navigation.component';

/**
 *  Mobile menu with a hamburger and floating FAB button
 *  TODO: refactor to make FAB a separate component
 */
@Component({
  selector: 'td-wb-mobile-menu',
  templateUrl: 'mobile-menu.component.html',
  styleUrls: ['mobile-menu.component.scss'],
  imports: [
    TranslatePipe,
    TranslateDirective,
    IconComponent,
    MenuSearchNavigationComponent,
    MobileFlyoutMenuComponent,
    MobileFlyoutMenuNavComponent,
    DocumentNoScrollDirective,
    IfFeatureDirective,
    IfNoFeatureDirective,
    NgClass,
    DividerComponent,
    MessagesCounterComponent,
    ButtonComponent,
    HrefDirective,
    MobileFlyoutMenuNavPanDirective,
    NgTemplateOutlet,
    TranslatePipe,
    BadgeComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MobileMenuComponent implements OnInit, OnDestroy {

  private static readonly TRADING: string = 'trading';
  private static readonly FRACTIONAL_LINK: string = '#/page/fractional-landing';
  protected logoUrl: string;
  protected logoLabelTranslationId: string;

  @ViewChild('flyoutMenu') flyoutMenu: MobileFlyoutMenuComponent;
  @ViewChild('documentNoScroll') documentNoScroll: DocumentNoScrollDirective;
  @ViewChild('mobileFlyoutSubMenu') mobileFlyoutSubMenu: MobileFlyoutMenuNavComponent;

  readonly MESSAGE_MENU_ID: string = 'messages';
  /**
   * Menus to display
   */
  menu: Menu;

  /**
   * Logged in client name
   */
  clientName: string;

  /**
   * Hidden - true to completely hide menu & footer, ie when site is embedded
   */
  isHidden: boolean;

  // TODO deprecate
  // eslint-disable-next-line @typescript-eslint/naming-convention
  readonly MenuItemType: typeof MenuItemType = MenuItemType;

  isExpanded: boolean = false;

  showChat: boolean = false;

  /**
   * A subscription to the [Client Profile Service's onClientNameChange observable]{@link ClientProfileService#onClientNameChange}
   */
  private clientNameSubscription: Subscription;
  private readonly disableFractionalTrading: boolean = false;

  constructor(private menuService: MenuService,
              private clientProfileService: ClientProfileService,
              private changeDetectorRef: ChangeDetectorRef,
              private featureService: IfFeatureService,
              private securityService: SecurityService,
              private dataAnalyticsTaggingService: DataAnalyticsTaggingService,
              private chatService: ChatService,
              private dataAnalyticsService: DataAnalyticsService) {
    this.disableFractionalTrading = this.featureService.getFeatureFlag<boolean>('disableFractionalTrading');
    this.logoUrl = this.menuService.getLogoUrl();
    this.logoLabelTranslationId = this.menuService.getLogoLabelTranslationId();
    this.showChat = this.chatService.hasAccessToChat();
  }

  /**
   * This function initializes the list of menus and required variables for displaying menu
   */
  ngOnInit(): void {
    this.isHidden = this.menuService.isHidden();
    this.menu = this.menuService.getMenu();
    this.clientName = this.clientProfileService.getFullName(true);
    this.clientNameSubscription = this.clientProfileService.onPreferredNameChange.subscribe((): void => {
      this.clientName = this.clientProfileService.getFullName(true);
      this.changeDetectorRef.markForCheck();
    });

    if (this.disableFractionalTrading) {
      const research: Array<MenuPanelItem> = this.menu.mobileNavigation.filter((item: MenuPanelItem) =>
        item.id === MobileMenuComponent.TRADING);
      const researchLinks = research[0].menu[0].item;
      const fractionalLinkIndex = researchLinks.findIndex((item: MenuItem) => item.href === MobileMenuComponent.FRACTIONAL_LINK);
      if (fractionalLinkIndex !== -1) {
        researchLinks.splice(fractionalLinkIndex, 1);
      }
    }
  }

  ngOnDestroy(): void {
    this.clientNameSubscription?.unsubscribe();
  }

  onMobileMenuClick(item: MenuPanelItem): void {
    this.dataAnalyticsTaggingService.emitTaggingEvent(
      DataAnalyticsTaggingConstants.getLinkClickEventData(`tdw:et:hamburgermenu:${item.text}`));
  }

  open(event: MouseEvent): void {
    this.flyoutMenu.open();
    event.preventDefault();
  }

  openedChange(expanded: boolean): void {
    this.documentNoScroll.disableScroll(expanded);
    this.isExpanded = expanded;
  }

  openSub(event: MouseEvent, item: MenuPanelItem): void {
    this.mobileFlyoutSubMenu.open(item);
    this.onMobileMenuClick(item);
    event.preventDefault();
    event.stopPropagation();
  }

  closeSub(event: MouseEvent): void {
    this.mobileFlyoutSubMenu.close();
    event.preventDefault();
    event.stopPropagation();
  }

  isExternalLink(item: MenuPanelItem): boolean {
    return item.href !== undefined && (item.external === true || !!item.target) &&
      item.type !== MenuItemType.CONTACT_NUMBER && item.type !== MenuItemType.LINK;
  }

  isLocalLink(item: MenuPanelItem): boolean {
    return item.href !== undefined && item.type !== MenuItemType.CONTACT_NUMBER && item.type !== MenuItemType.LINK &&
      (item.external === undefined || item.external === false);
  }

  isMenuGroup(item: MenuPanelItem): boolean {
    return item.menu?.length > 0;
  }

  hasProfilePermission(): boolean {
    return this.securityService.hasPermission('CLIENT_FOCUS_REFORM');
  }

  openChat(): void {
    this.dataAnalyticsService.emitLinkClickEvent('contactus:chatwithaliveagent');
    this.chatService.openChat('Contact Us');
  }

  protected showNewBadge(value: string): boolean {
    return this.menuService.showNewBadge(value);
  }
}
